import { FleetLoggerStore } from '../FleetLogger';  
import { DeliveriesEventArgs, DeliveriesStore, Delivery } from './DeliveriesStore';
import "./DeliveryListElement.less";
import { DeliveryControlElement } from './DeliveryControlElement';
import { DeviceDetails } from '../FleetLogger/FleetLoggerStore';
import { DeliveryInfoElement } from './DeliveryInfoElement';

export class DeliveryListElement extends HTMLElement {
    public static tag = "delivery-list";
    private _expandButton: HTMLButtonElement;
    private _refereshButton: HTMLButtonElement;
    private _deliveriesList: HTMLElement;
    private _header: HTMLElement;
    private _lastsRefresh: Date;
    private _lastUpdated: HTMLElement;
    private _abortController: AbortController;
    private _baseTitle: string;

    connectedCallback() { 
         // initial view
        this.innerHTML = this.view();

        // element references
        this._header = this.querySelector("header") as HTMLElement;
        this._expandButton = this.querySelector("[name=expand]") as HTMLButtonElement;
        this._refereshButton = this.querySelector("[name=refresh]") as HTMLButtonElement;
        this._deliveriesList = this.querySelector("[name=deliveries-list]") as HTMLElement;

        // events
        FleetLoggerStore.instance.addEventListener("logger-started", this.onUpdate);
        this._expandButton?.addEventListener("click", this.onToggle);
        this._refereshButton.addEventListener("click", this.onRefresh);
        this.addEventListener("click", this.onClick);

        DeliveriesStore.instance.addEventListener("deliveries-updated", this.onDeliveriesUpdated);
        DeliveriesStore.instance.addEventListener("deliveries-updating", this.onDeliveriesUpdating);
        DeliveriesStore.instance.addEventListener("deliveries-updating-canceled", this.onDeliveriesUpdateSkipped);
        DeliveriesStore.instance.addEventListener("deliveries-partial-update", this.onDeliveriesPartialUpdate);

        (<any>window).deliveriesStore = DeliveriesStore.instance;
        this._abortController = DeliveriesStore.instance.subscribeToUpdates(this.getAttribute("route-name"));
    }

    disconnectedCallback() { 
        this._expandButton?.removeEventListener("click", this.onToggle);
        this._refereshButton.removeEventListener("click", this.onUpdate);
        this.removeEventListener("click", this.onClick);

        DeliveriesStore.instance.removeEventListener("deliveries-updated", this.onDeliveriesUpdated);
        DeliveriesStore.instance.removeEventListener("deliveries-updating", this.onDeliveriesUpdating);
        DeliveriesStore.instance.removeEventListener("deliveries-updating-canceled", this.onDeliveriesUpdateSkipped);
        DeliveriesStore.instance.removeEventListener("deliveries-partial-update", this.onDeliveriesPartialUpdate);

        this._abortController?.abort();
    }

    private onClick = (event: PointerEvent) => { 
        const target = event.target as HTMLElement;
        const deliveryInfo = target.closest("[invoice-id]");

        if (!deliveryInfo)
            return;

        if (deliveryInfo.hasAttribute("sliding"))
            return;
        
        const invoiceId = deliveryInfo.getAttribute("invoice-id");
        
        const deliveryControl = new DeliveryControlElement();

        if (this.hasAttribute("route-name"))
            deliveryControl.setAttribute("route-name", this.getAttribute("route-name"));
        
        deliveryControl.setAttribute("invoice-id", invoiceId); 
        deliveryControl.setAttribute("delivery-type", deliveryInfo.getAttribute("delivery-type"));
        deliveryControl.setAttribute("order-id", deliveryInfo.getAttribute("order-id"));
        this.parentElement.insertAdjacentElement("afterbegin",  deliveryControl);
    }

    private onToggle = () => { 
        if (this.getAttribute("state") === "maximized") {
            this.setAttribute("state", "minimized");
        } else { 
            this.setAttribute("state", "maximized");
            this.querySelector("delivery-info[status=ongoing]")?.scrollIntoView(true);
        }
    };

    private showStatus = (status: string) => { 
        this._header.innerText = status;
    }

    private onRefresh = async () => await DeliveriesStore.instance.refresh(this.getAttribute("route-name"));
    
    private onDeliveriesUpdateSkipped = () => { 
        this.toggleAttribute("refreshing", false);
        this.showStatus(this._baseTitle);
        this.updateLastUpdated();
    }

    private updateLastUpdated = () => { 
        this._lastsRefresh = new Date();
        this._lastUpdated.innerText = `Last updated ${this.shortTime(this._lastsRefresh)}`;
    }

    

    private onDeliveriesPartialUpdate = (event: CustomEvent<DeliveriesEventArgs>) => { 
        if (this.hasAttribute("route-name")) { 
            if (event.detail.routeName !== this.getAttribute("route-name"))
                return;
        }

        for (const delivery of event.detail.deliveries) {
            const deliveryInfo = this.querySelector(`[invoice-id="${delivery.invoiceNo}"]`);
            if (!deliveryInfo)
                continue;

            if (delivery.currentTarget) { 
                this.querySelector(".next-delivery").remove();
            }

            deliveryInfo.outerHTML = this.deliveryView(delivery);
        }
    }
    
    private onDeliveriesUpdated = async (event: CustomEvent<DeliveriesEventArgs>) => { 
        if (this.hasAttribute("route-name")) { 
            if (event.detail.routeName !== this.getAttribute("route-name"))
                return;
        }

        this._deliveriesList.innerHTML = this.deliveriesView(event.detail.deliveries);

        if (this.getAttribute("state") === "maximized")
            requestAnimationFrame(() => this.querySelector("delivery-info[status=ongoing]")?.scrollIntoView(true));
        
        this._baseTitle = `Deliveries (${event.detail.deliveries.length})`
        this.showStatus(this._baseTitle);
        this.toggleAttribute("refreshing", false);

        this._deliveriesList.insertAdjacentHTML("beforeend", this.infoView());

        this._lastUpdated = this.querySelector(".last-update") as HTMLElement;
        this.updateLastUpdated();
    }

    private onDeliveriesUpdating = (event: CustomEvent<string>) => {
        if (this.hasAttribute("route-name")) { 
            if (event.detail !== this.getAttribute("route-name"))
                return;
        }

        this.toggleAttribute("refreshing", true);
        this.showStatus("Updating deliveries ...");        
    }

    private onUpdate = async (event: CustomEvent<DeviceDetails>) => { 
        
        if (this.getAttribute("state") !== "maximized")
            this.setAttribute("state", "minimized");
        
        DeliveriesStore.instance.updateRouteName(event.detail.name, event.detail.initials);
        DeliveriesStore.instance.refresh(this.getAttribute("route-name"));        
    }

    private deliveriesView = (deliveries: Delivery[]) => { 
        return deliveries.map(delivery => this.deliveryView(delivery)).join("");
    }

    private formatDate = (dateString: string) => {
        if (!dateString)
            return "";

        let date: Date;
        
        if (dateString.indexOf("+") !== - 1)
            date = new Date(dateString);
        else
            date = new Date(`${dateString}Z`);

        return this.format(date);
    }

    private format = (date: Date) => {
        if (!date)
            return "";

        // check if date is today
        const today = new Date();
        if (date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear())
            return `${date.getHours()}:${this.pad(date.getMinutes())}`;

        return `${date.getDate()}/${date.getMonth() + 1}`;
    }

    private deliveryView = (delivery: Delivery) => `
        <${DeliveryInfoElement.tag} invoice-id="${delivery.invoiceNo}" status="${delivery.status}" delivery-type="${delivery.type}" order-id="${delivery.orderNo}">
            <div>
                <div class="status">
                    <span name=complete>${require("./Images/complete-delivery.svg")}</span>
                    <span name=ongoing>${require("./Images/ongoing-delivery.svg")}</span>
                    <span name=unloaded>${require("./Images/unloaded-delivery.svg")}</span>
                </div>
            
                <div class=order-delivery>
                    <div class=order-no>${delivery.orderNo}</div>
                    ${delivery.deliveredAt !== "" ? `
                        <div class=delivery-date>${this.formatDate(delivery.deliveredAt)}</div>
                    ` : `
                        <div class=eta-date title="Estimated time of arrival">~ ${this.format(delivery.eta)}</div>
                    `}
                    <div class="type" ${delivery.type?.toLowerCase()}>${delivery.type ?? ""}</div>
                </div>
                <div class=priority>${delivery.priority}</div>
                <div class=address> 
                    <div class=delivery-name>${delivery.deliveryName}</div>
                    <div class=delivery-address>${delivery.deliveryAddress} ${delivery.deliveryAddress2 || ""}</div>
                    <div class=delivery-zip-code>${delivery.deliveryZipCode} ${delivery.deliveryCity}</div> 
                </div>

                ${delivery.currentTarget ? `<div class=next-delivery><div></div></div>` : ""}
            </div>
        </${DeliveryInfoElement.tag}>
    `;

    private view = () => `
        <button class=icon name=refresh type=button>${require("./Images/refresh.svg")}</button>
        ${this.hasAttribute("no-close") ? "" : `<button class=icon name=expand type=button>${require("./Images/up-arrow.svg")}</button>`}
        <header>Deliveries</header>
        <div name=deliveries-list></div>
    `;

    private infoView = () => `
        <div class=last-update></div>
        <div class=info>
            <div>${require("./Images/drag-right.svg")}Loaded</div>
            <div>Unloaded${require("./Images/drag-left.svg")}</div>
        </div>
        `;


    private pad = (no: number, wantedLength = 2, symbol = "0") => {
        const source = no.toString();
        return source.length >= wantedLength ? source : new Array(wantedLength - source.length + 1).join(symbol) + source;
    }

    private shortTime = (date: Date): string => `${date.getHours()}:${this.pad(date.getMinutes())}`;

    private shortDateTime = (date: Date): string => `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${this.pad(date.getMinutes())}`;
}

customElements.define(DeliveryListElement.tag, DeliveryListElement);